import { DateTime } from 'luxon';

import {
  RequestQuery,
  AvailabilitiesState,
  AvailabilitiesForAppointmentTypeRequestOptions,
  AvailabilitiesResponseData,
} from 'types';

type AppointmentTypeAvailabilities = {
  [key: string]: {
    [key: string]: number[];
  };
};
export const transformAvailabilities = (
  availabilities: AvailabilitiesResponseData,
): AvailabilitiesState =>
  Object.keys(availabilities).reduce((accum: AvailabilitiesState, appointmentTypeId: string) => {
    accum[appointmentTypeId] = Object.keys(availabilities[appointmentTypeId]).reduce(
      (_appointmentTypeAvailabilities: AppointmentTypeAvailabilities, dateTime: string) => {
        const [date, time] = dateTime.split('T');
        // eslint-disable-next-line no-param-reassign
        _appointmentTypeAvailabilities[date] = {
          ..._appointmentTypeAvailabilities[date],
          [time.slice(0, -3)]: availabilities[appointmentTypeId][dateTime],
        };
        return _appointmentTypeAvailabilities;
      },
      {},
    );
    return accum;
  }, {});

export const mergeAvailabilities = (
  prevAvailabilities: AvailabilitiesState,
  nextAvailabilities: AvailabilitiesState,
  dateRange?: {
    startDate: string;
    endDate: string;
  },
): AvailabilitiesState =>
  Object.keys(nextAvailabilities).reduce(
    (accum: AvailabilitiesState, appointmentType: string): AvailabilitiesState => {
      accum[appointmentType] = Object.keys(prevAvailabilities[appointmentType]).reduce(
        (
          _availabilitiesForAppointmentType: AppointmentTypeAvailabilities,
          date: string,
        ): AppointmentTypeAvailabilities => {
          if (dateRange) {
            const { startDate, endDate } = dateRange;
            if (date < startDate || date > endDate) {
              // eslint-disable-next-line no-param-reassign
              _availabilitiesForAppointmentType[date] =
                prevAvailabilities?.[appointmentType]?.[date];
            }
          }
          return _availabilitiesForAppointmentType;
        },
        nextAvailabilities[appointmentType],
      );
      return accum;
    },
    { ...prevAvailabilities },
  );

export const getNextAvailabilitiesQuery = (
  practiceId: string | number,
  options: { force?: boolean; appointmentTypeIds?: string[]; serviceLocationId?: number } = {},
): RequestQuery => {
  const { force, appointmentTypeIds, serviceLocationId } = options;
  return {
    force,
    options: { dontCamelizeKeys: true },
    body: { appt_type_ids: appointmentTypeIds, serviceLocationId },
    queryKey: `getNextAvailabilities:${practiceId}-${serviceLocationId}`,
    transform: ({ data }): { availabilities: AvailabilitiesState } => ({
      availabilities: transformAvailabilities(data),
    }),
    update: {
      availabilities: (
        prevAvailabilities: AvailabilitiesState,
        newAvailabilities: AvailabilitiesState,
      ): AvailabilitiesState =>
        prevAvailabilities
          ? mergeAvailabilities(prevAvailabilities, newAvailabilities)
          : newAvailabilities,
    },
    url: `book/api/${practiceId}/availabilities/next-available`,
  };
};

export const getAvailabilitiesForAppointmentTypeQuery = (
  practiceId: string | number,
  options: AvailabilitiesForAppointmentTypeRequestOptions,
): RequestQuery => {
  const {
    startDate = DateTime.fromObject({}).toISODate(),
    endDate = DateTime.fromObject({}).plus({ days: 3 }).toISODate(),
    appointmentTypeId,
    serviceLocationId,
    force,
  } = options;
  return {
    body: {
      startDate,
      endDate,
      serviceLocationId,
      apptTypeId: appointmentTypeId,
    },
    force,
    options: { dontCamelizeKeys: true },
    queryKey: `getAvailabilitiesForAppointmentType:${practiceId}-${startDate}-${endDate}-${appointmentTypeId}-${serviceLocationId}`,
    transform: ({ data }): { availabilities: AvailabilitiesState } => ({
      availabilities: transformAvailabilities({ [appointmentTypeId]: data }),
    }),
    update: {
      availabilities: (
        prevAvailabilities: AvailabilitiesState,
        newAvailabilities: AvailabilitiesState,
      ): AvailabilitiesState =>
        mergeAvailabilities(prevAvailabilities, newAvailabilities, { startDate, endDate }),
    },
    url: `book/api/${practiceId}/availabilities`,
  };
};
